import { gql } from '@apollo/client';

import { MembershipTermRenewalSetting } from '@/graphql-types/globalTypes';
import { MembershipTermPaymentAlert } from '@/graphql-types/globalTypes';

/**
 * Automatic invoicing UI helpers.
 *
 * When both org-level automatic invoicing switches are off, members with
 * renewalSetting INVOICING in the DB are shown and treated like RENEWAL_REMINDERS
 * in the UI (dropdown label, renewal option selection). The saved DB value stays
 * INVOICING so invoicing behavior resumes when admin re-enables the switches.
 *
 * All helpers are display-only guards: they never write RENEWAL_REMINDERS over
 * INVOICING on save. When auto invoicing is available, helpers are no-ops and
 * existing INVOICING UI/behavior is unchanged.
 */

export const GET_AUTOMATIC_INVOICING_AVAILABILITY = gql`
  query MembershipOrderRenewalOptions_AutomaticInvoicingAvailability {
    getInvoiceSetting {
      automaticInvoicingEnabled
      automaticInvoicingByApprovalEnabled
    }
  }
`;

export type MembershipOrderRenewalOptionValue =
  | MembershipTermRenewalSetting.AUTOMATIC_RENEWAL
  | MembershipTermRenewalSetting.RENEWAL_REMINDERS
  | MembershipTermRenewalSetting.INVOICING;

export const getRenewalSettingFromOrder = (order: {
  renewalSetting?: MembershipTermRenewalSetting | null;
  isAutoPayEnabled: boolean;
}): MembershipOrderRenewalOptionValue => {
  if (order.renewalSetting === MembershipTermRenewalSetting.INVOICING) {
    return MembershipTermRenewalSetting.INVOICING;
  }
  if (order.renewalSetting === MembershipTermRenewalSetting.RENEWAL_REMINDERS) {
    return MembershipTermRenewalSetting.RENEWAL_REMINDERS;
  }
  if (order.renewalSetting === MembershipTermRenewalSetting.AUTOMATIC_RENEWAL) {
    return MembershipTermRenewalSetting.AUTOMATIC_RENEWAL;
  }
  return order.isAutoPayEnabled
    ? MembershipTermRenewalSetting.AUTOMATIC_RENEWAL
    : MembershipTermRenewalSetting.RENEWAL_REMINDERS;
};

export const isAutoPayEnabledForRenewalSetting = (
  renewalSetting: MembershipOrderRenewalOptionValue
): boolean => renewalSetting === MembershipTermRenewalSetting.AUTOMATIC_RENEWAL;

type MembershipOrderOwnerWithTerms = {
  __typename?: string;
  membershipData?: {
    relevantMembershipTerms?: Array<{ hasPriceOverride?: boolean | null }>;
  } | null;
};

/**
 * True when auto-renewal and automatic invoicing must be disabled due to price
 * override on the current order or prior override history on membership terms.
 */
export const isMembershipOrderBlockedByOverrideHistory = (options: {
  adjustedPrice?: unknown;
  owner?: MembershipOrderOwnerWithTerms | null;
}): boolean => {
  if (options.adjustedPrice) {
    return true;
  }
  const terms =
    options.owner?.__typename === 'User' ||
    options.owner?.__typename === 'Organization'
      ? options.owner.membershipData?.relevantMembershipTerms ?? []
      : [];
  return terms.some((term) => Boolean(term.hasPriceOverride));
};

type AutomaticInvoicingAvailabilityQuery = {
  getInvoiceSetting?: {
    automaticInvoicingEnabled?: boolean | null;
    automaticInvoicingByApprovalEnabled?: boolean | null;
  } | null;
};

export const getAutomaticInvoicingAvailabilityState = (options: {
  data?: AutomaticInvoicingAvailabilityQuery | null;
  error?: unknown;
  loading?: boolean;
}) => {
  const invoiceSetting = options.data?.getInvoiceSetting;
  const isLoaded =
    Boolean(invoiceSetting) ||
    Boolean(options.error) ||
    options.loading === false;

  const isAvailable =
    isLoaded &&
    !options.error &&
    (!!invoiceSetting?.automaticInvoicingEnabled ||
      !!invoiceSetting?.automaticInvoicingByApprovalEnabled);

  return {
    isLoaded,
    isAvailable,
    // True only after settings load and both switches are off (safe for display fallback).
    shouldFallbackInvoicingToReminders: isLoaded && !isAvailable,
  };
};

/**
 * Maps persisted renewalSetting to what the UI should show. INVOICING is shown as
 * RENEWAL_REMINDERS only when automatic invoicing fallback applies (or monthly /
 * free / override blocks invoicing). Does not change the value saved to the API.
 */
export const getDisplayRenewalSetting = <
  T extends MembershipTermRenewalSetting
>(options: {
  renewalSetting: T;
  shouldFallbackInvoicingToReminders?: boolean;
  isMonthlyPayment?: boolean;
  isFreeMembership?: boolean;
  isBlockedByOverride?: boolean;
}): T => {
  if (options.isBlockedByOverride) {
    return MembershipTermRenewalSetting.RENEWAL_REMINDERS as T;
  }

  if (
    options.renewalSetting === MembershipTermRenewalSetting.INVOICING &&
    (options.shouldFallbackInvoicingToReminders ||
      options.isMonthlyPayment ||
      options.isFreeMembership)
  ) {
    // Display-only: show "Send reminders for renewal" while DB keeps INVOICING.
    return MembershipTermRenewalSetting.RENEWAL_REMINDERS as T;
  }

  return options.renewalSetting;
};

/**
 * Hide manual "Receive Payment" when the member chose invoicing and org automatic
 * invoicing is on — payment is handled via invoice creation, not the membership
 * order receive-payment flow. When org switches are off, INVOICING falls back to
 * renewal reminders and manual payment stays available.
 */
export const shouldHideReceivePaymentForAutomaticInvoicing = (options: {
  renewalSetting?: MembershipTermRenewalSetting | null;
  automaticInvoicingAvailability: ReturnType<
    typeof getAutomaticInvoicingAvailabilityState
  >;
  paymentAlert?: MembershipTermPaymentAlert | null;
}): boolean => {
  if (options.renewalSetting !== MembershipTermRenewalSetting.INVOICING) {
    return false;
  }
  // Avoid flashing Receive Payment while invoice settings are loading.
  if (!options.automaticInvoicingAvailability.isLoaded) {
    return true;
  }
  if (!options.automaticInvoicingAvailability.isAvailable) {
    return false;
  }
  // Allow retry when a prior payment attempt failed.
  if (options.paymentAlert === MembershipTermPaymentAlert.PAYMENT_FAILED) {
    return false;
  }
  return true;
};

/**
 * Member-facing renewal reminder modal: hide for INVOICING when org automatic
 * invoicing is on (invoice cron handles renewal). Show when both admin switches
 * are off so INVOICING falls back to renewal-reminder behavior.
 */
export const shouldShowRenewalReminderModalForTerm = (options: {
  renewalSetting?: MembershipTermRenewalSetting | null;
  automaticInvoicingAvailability: ReturnType<
    typeof getAutomaticInvoicingAvailabilityState
  >;
}): boolean => {
  if (options.renewalSetting !== MembershipTermRenewalSetting.INVOICING) {
    return true;
  }
  if (!options.automaticInvoicingAvailability.isLoaded) {
    return false;
  }
  return options.automaticInvoicingAvailability.shouldFallbackInvoicingToReminders;
};
